import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import KaagazyLogoMobile from "../../assets/logo-teal-mobile.png";
import KaagazyLogoWhite from "../../assets/logo-white.png";
import { deriveOrganizationName, useClinic } from "../../contexts/ClinicContext";
import { OnboardingAPI } from "../../services/onboarding.service";
import { refreshOnboardingCache } from "../../components/RequireAuth";
import { OrganizationAPI } from "../../services/organization.service";
import { SettingsAPI } from "../../services/settings.service";
import { INDIAN_STATES } from "../../constants/indianStates";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ClinicDetails {
  clinicName: string;
  companySize: string;
  streetAddress: string;
  city: string;
  pincode: string;
  state: string;
  website: string;
}

interface ValidationErrors {
  companySize?: string;
  website?: string;
}

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */

const ClinicDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organizationName, activeClinic, switchClinic } = useClinic();

  /**
   * Branch mode — reached from "Add a clinic" in the switcher.
   *
   * Without it this page reads "you already have a clinic" as "you are editing
   * it": it pre-fills the existing clinic and submits a PATCH. An owner adding
   * a second branch would silently rename their working clinic and create
   * nothing. Branch mode skips the pre-fill and POSTs a new clinic instead.
   *
   * Carried in the query string rather than router state so a refresh or a
   * shared link keeps the mode. Losing it mid-form is exactly the overwrite
   * this flag exists to prevent.
   */
  const isBranchMode = searchParams.get("mode") === "branch";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only asked for when the caller has no organization yet — this add is what
  // creates one, around their existing clinic.
  const [orgName, setOrgName] = useState("");

  // ── Logo upload (optional) ──────────────────────────────────────────
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const [details, setDetails] = useState<ClinicDetails>({
    clinicName: "",
    companySize: "",
    streetAddress: "",
    city: "",
    pincode: "",
    state: "",
    website: "",
  });

  // On mount, check if the user already has a clinic and pre-fill the form.
  useEffect(() => {
    const loadExistingClinic = async () => {
      // Branch mode: never pre-fill and never enter edit mode. The caller
      // certainly HAS a clinic — that is the whole premise — so the usual
      // "clinic exists, therefore edit it" inference is exactly wrong here.
      if (isBranchMode) {
        setOrgName(organizationName ?? deriveOrganizationName(activeClinic));
        setLoading(false);
        return;
      }
      try {
        const status = await OnboardingAPI.getStatus();
        if (status.clinic_id) {
          const clinic = await OnboardingAPI.getClinic();
          setDetails({
            clinicName: clinic.name,
            companySize: clinic.company_size ?? "",
            streetAddress: "",
            city: clinic.location,
            pincode: "",
            state: "",
            website: clinic.website ?? "",
          });
          if (clinic.logo_url) setLogoPreview(clinic.logo_url);
          setIsEditMode(true);
        }
      } catch {
        // No existing clinic or fetch failed — show empty form.
      } finally {
        setLoading(false);
      }
    };

    loadExistingClinic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBranchMode, organizationName, activeClinic?.name]);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const updateField = <K extends keyof ClinicDetails>(
    key: K,
    value: ClinicDetails[K]
  ) => {
    setDetails((prev) => ({ ...prev, [key]: value }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[key as keyof ValidationErrors]) {
      setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const validateCompanySize = (value: string): string | undefined => {
    if (!value.trim()) return undefined; // Required validation handled separately
    if (!/^\d+$/.test(value)) {
      return "Company size must be a number";
    }
    return undefined;
  };

  const validateWebsite = (value: string): string | undefined => {
    if (!value.trim()) return undefined; // Optional field
    const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/.*)?$/;
    if (!urlPattern.test(value)) {
      return "Please enter a valid website URL";
    }
    return undefined;
  };

  const handleNext = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!details.clinicName.trim()) {
      setError("Please enter your clinic name.");
      return;
    }
    if (!details.companySize.trim()) {
      setError("Please enter the company size.");
      return;
    }
    if (!details.streetAddress.trim()) {
      setError("Please enter the street address.");
      return;
    }
    if (!details.city.trim()) {
      setError("Please enter the city.");
      return;
    }
    if (!details.pincode.trim()) {
      setError("Please enter the pincode.");
      return;
    }
    if (!details.state.trim()) {
      setError("Please enter the state.");
      return;
    }

    // Validate company size and website format
    const companySizeError = validateCompanySize(details.companySize);
    const websiteError = validateWebsite(details.website);

    if (companySizeError || websiteError) {
      setValidationErrors({
        companySize: companySizeError,
        website: websiteError,
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: details.clinicName.trim(),
        location: details.city.trim(),
        address: [details.streetAddress, details.city, details.pincode, details.state]
          .filter(Boolean)
          .join(", ") || undefined,
        company_size: details.companySize.trim() || undefined,
        website: details.website.trim() || undefined,
      };

      if (isBranchMode) {
        // A branch is a NEW clinic under the caller's organization. This is
        // the only route that adds one: POST /onboarding/clinic refuses when
        // the user already has a clinic, and reassigns their clinic_id if it
        // did not.
        const result = await OrganizationAPI.addClinic({
          name: payload.name,
          location: payload.location,
          ...(payload.address ? { address: payload.address } : {}),
          ...(organizationName ? {} : { organization_name: orgName.trim() }),
        });

        // Switch into it before continuing. The remaining steps (documents,
        // plan) act on the request's active clinic, so without this the new
        // branch's registration would be filed against the clinic we are
        // currently in. switchClinic drains the offline queue, flushes the
        // local cache and reloads — RequireAuth then routes to
        // /onboarding/documents because the branch is documents_pending.
        await switchClinic(result.clinic.clinic_id);
        return;
      }

      if (isEditMode) {
        await OnboardingAPI.updateClinic(payload);
      } else {
        await OnboardingAPI.saveClinic(payload);
        await refreshOnboardingCache();
      }

      // Upload logo if the user picked one — non-fatal but warn if it fails
      if (logoFile) {
        try {
          await SettingsAPI.uploadClinicLogo(logoFile);
        } catch {
          setError("Clinic details saved, but logo upload failed. You can re-upload from Settings.");
          setSaving(false);
          return;
        }
      }

      navigate("/onboarding/documents");
    } catch (err: any) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7]">
        <div className="text-sm text-slate-500">Loading your clinic details…</div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex">
      {/* ───── Left panel (40%) ───── */}
      <div
        className="hidden md:flex w-[40%] h-full flex-col relative"
        style={{
          background:
            "linear-gradient(180deg, #6366f1 0%, #007cf0 30%, #00b3ff 60%, #00dfd8 100%)",
        }}
      >
        {/* Branding */}
        <div className="absolute top-0 left-6 lg:left-6">
          <img
            src={KaagazyLogoWhite}
            alt="Kaagazy"
            className="h-72 object-contain"
            style={{ mixBlendMode: "screen", marginTop: "-60px" }}
          />
        </div>

        {/* Hero text */}
        <div className="flex-1 flex flex-col justify-center px-10 lg:px-14 text-white">
          <p className="text-base font-normal text-white/60 mb-3">Step 1 of 3</p>
          <h1 className="text-[40px] lg:text-[48px] font-medium leading-[1.1] tracking-tight">
            {isEditMode ? "Update your\nclinic info" : "Let's see what\ndo you do?"}
          </h1>
        </div>
      </div>

      {/* ───── Right panel (60%) ───── */}
      <div className="flex flex-col w-full md:w-[60%] bg-white h-full overflow-y-auto items-center">
        <div className="flex-1 flex flex-col justify-center px-8 md:px-14 py-8 sm:py-12 max-w-[600px] w-full">
          {/* Mobile-only branding */}
          <div className="md:hidden mb-4 flex justify-center">
            <img
              src={KaagazyLogoMobile}
              alt="Kaagazy"
              className="h-10 object-contain"
            />
          </div>

          {/* Heading */}
          <h2 className="text-2xl font-semibold text-slate-900 mb-2 leading-tight">
            {isEditMode ? "Update Clinic Details" : "What Do You Do?"}
          </h2>
          {isEditMode && (
            <p className="text-sm text-slate-500 mb-8">
              Your clinic details are shown below. You can update them before continuing.
            </p>
          )}
          {!isEditMode && <div className="mb-8" />}

          {/* Form */}
          <form onSubmit={handleNext} className="space-y-3 sm:space-y-4">
            {/* Clinic name */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-500">
                Clinic name <span className="text-red-500">*</span>
              </label>
              <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 sm:py-3 focus-within:border-blue-400 transition-colors">
                <input
                  type="text"
                  value={details.clinicName}
                  onChange={(e) => updateField("clinicName", e.target.value)}
                  className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                  placeholder="SwasthiQ Clinic"
                />
              </div>
            </div>

            {/* Company Size */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-500">
                Company size <span className="text-red-500">*</span>
              </label>
              <div className={`relative rounded-xl border ${validationErrors.companySize ? 'border-red-300' : 'border-slate-200'} bg-white px-3.5 py-2.5 sm:py-3 focus-within:border-blue-400 transition-colors`}>
                <input
                  type="text"
                  value={details.companySize}
                  onChange={(e) => updateField("companySize", e.target.value)}
                  className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                  placeholder="e.g. 5"
                />
              </div>
              {validationErrors.companySize && (
                <p className="mt-1.5 text-xs text-red-600">{validationErrors.companySize}</p>
              )}
            </div>

            {/* Street address */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-500">
                Street address <span className="text-red-500">*</span>
              </label>
              <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 sm:py-3 focus-within:border-blue-400 transition-colors">
                <input
                  type="text"
                  value={details.streetAddress}
                  onChange={(e) => updateField("streetAddress", e.target.value)}
                  className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                  placeholder="123 MG Road"
                />
              </div>
            </div>

            {/* City / Pincode / State row */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-500">
                City / Pincode / State <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 sm:py-3 focus-within:border-blue-400 transition-colors">
                  <input
                    type="text"
                    value={details.city}
                    onChange={(e) => updateField("city", e.target.value)}
                    className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                    placeholder="City"
                  />
                </div>
                <div className="relative rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 sm:py-3 focus-within:border-blue-400 transition-colors">
                  <input
                    type="text"
                    value={details.pincode}
                    onChange={(e) => updateField("pincode", e.target.value)}
                    className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                    placeholder="Pincode"
                  />
                </div>
                <div className="relative rounded-xl border border-slate-200 bg-white pl-3.5 pr-8 py-2.5 sm:py-3 focus-within:border-blue-400 transition-colors">
                  <select
                    value={details.state}
                    onChange={(e) => updateField("state", e.target.value)}
                    className={`w-full border-none bg-transparent text-base outline-none appearance-none ${details.state ? "text-slate-900" : "text-slate-400"}`}
                  >
                    <option value="">State</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Website */}
            <div>
              <div className={`rounded-xl border ${validationErrors.website ? 'border-red-300' : 'border-slate-200'} bg-white px-3.5 py-2.5 sm:py-3 focus-within:border-blue-400 transition-colors`}>
                <input
                  type="text"
                  value={details.website}
                  onChange={(e) => updateField("website", e.target.value)}
                  className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                  placeholder="Website (optional)"
                />
              </div>
              {validationErrors.website && (
                <p className="mt-1.5 text-xs text-red-600">{validationErrors.website}</p>
              )}
            </div>

            {/* Clinic Logo — optional */}
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">
                Clinic Logo <span className="text-slate-400 font-normal">— optional, JPG or PNG</span>
              </p>
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className="flex items-center gap-3 w-full rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3.5 py-2.5 hover:border-blue-400 hover:bg-blue-50/40 transition-colors text-left"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 20.25h18M3.75 3h16.5A.75.75 0 0121 3.75v13.5A.75.75 0 0120.25 18H3.75A.75.75 0 013 17.25V3.75A.75.75 0 013.75 3z" />
                    </svg>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {logoPreview ? "Change logo" : "Upload clinic logo"}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    You can also do this later from Settings
                  </p>
                </div>
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleLogoSelect}
                className="hidden"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            {/* Next button */}
            <div className="pt-4 sm:pt-6">
              <button
                type="submit"
                disabled={saving}
                className="rounded-full bg-[#0066ff] px-8 sm:px-10 py-2.5 sm:py-3 text-sm sm:text-base font-medium text-white shadow-sm hover:bg-[#0051cc] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Next"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClinicDetailsPage;
